import React from 'react';
import { Bell, Settings, Trash2, Check, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NotificationItem } from './NotificationItem';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { HStack, VStack } from '@/components/core';

export const NotificationBell: React.FC = () => {
  const { unreadCount, notifications, markAllAsRead, deleteNotification } = useNotifications();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState(false);

  // Get recent notifications (max 10, including read/unread)
  const recentNotifications = notifications.slice(0, 10);

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/notifications/settings');
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await markAllAsRead();
  };

  const handleDeleteAll = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const unreadIds = recentNotifications.map(n => n.id);
    for (const id of unreadIds) {
      await deleteNotification(id);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="glass"
          size="sm"
          className="relative p-token-xs overflow-visible"
          aria-label={t('notifications.title')}
        >
          <Bell size={20} className="text-white" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1.5 -right-1 translate-x-[30%] h-5 min-w-5 px-1.5 rounded-full flex items-center justify-center text-xs bg-brand-primary border-0"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="glass-dropdown-panel rounded-token-card w-96 max-h-[600px] overflow-hidden p-0 flex flex-col"
        sideOffset={8}
      >
        {/* Header */}
        <div className="p-token-md border-b border-white/10 flex-shrink-0">
          <HStack align="center" justify="between">
            <h3 className="text-lg font-semibold design-text-primary">{t('notifications.title')}</h3>
            <HStack gap="sm" align="center">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-xs design-text-secondary hover:design-text-primary"
                >
                  <Check size={14} className="mr-token-xs" />
                  {t('notifications.markAllRead')}
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSettings}
                className="h-8 w-8 design-text-secondary hover:design-text-primary"
              >
                <Settings size={16} />
              </Button>
            </HStack>
          </HStack>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {recentNotifications.length === 0 ? (
            <div className="p-token-xl text-center">
              <Bell className="mx-auto h-12 w-12 text-white/20 mb-token-sm" />
              <p className="design-text-secondary text-sm">{t('notifications.emptyNew')}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="hover:bg-white/5 transition-colors cursor-pointer"
                  onClick={() => setIsOpen(false)}
                >
                  <NotificationItem
                    notification={notification}
                    onClose={() => setIsOpen(false)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <HStack align="center" justify="between" className="p-token-sm border-t border-white/10 flex-shrink-0">
          {notifications.length > 10 && (
            <p className="text-xs design-text-secondary">
              {t('notifications.moreNotifications', { count: notifications.length - 10 })}
            </p>
          )}
          <HStack gap="sm" align="center" className="ml-auto">
            {recentNotifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDeleteAll}
                className="text-xs design-text-secondary hover:text-error"
              >
                <Trash2 size={14} className="mr-token-xs" />
                {t('notifications.deleteAll')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleViewAll}
              className="text-xs design-text-accent hover:design-text-primary"
            >
              {t('notifications.viewAll')}
              <ChevronRight size={14} className="ml-token-xs" />
            </Button>
          </HStack>
        </HStack>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
